import React from 'react';
import {
  Card,
  CardContent,
  Checkbox,
  Avatar,
  Typography,
  Box,
  Chip
} from '@mui/material';
import { Child } from '../../types/parent';

interface ChildSelectionCardProps {
  child: Child;
  selected: boolean;
  onToggle: (childId: number) => void;
  disabled?: boolean;
  alreadyEnrolled?: boolean;
}

/**
 * ChildSelectionCard Component
 *
 * Displays a child as a selectable card with checkbox.
 * Used in parent enrollment flow to select which children to enroll.
 *
 * Features:
 * - Avatar with child's initials
 * - Child name and email
 * - Grade display if available
 * - Checkbox for selection
 * - Disabled state if already enrolled
 */
const ChildSelectionCard: React.FC<ChildSelectionCardProps> = ({
  child,
  selected,
  onToggle,
  disabled = false,
  alreadyEnrolled = false
}) => {
  const handleClick = () => {
    if (!disabled && !alreadyEnrolled) {
      onToggle(child.id);
    }
  };

  return (
    <Card
      data-testid={`child-selection-card-${child.id}`}
      sx={{
        cursor: disabled || alreadyEnrolled ? 'not-allowed' : 'pointer',
        opacity: disabled || alreadyEnrolled ? 0.6 : 1,
        border: selected ? 2 : 1,
        borderColor: selected ? 'primary.main' : 'divider',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: disabled || alreadyEnrolled ? 'divider' : 'primary.main',
          boxShadow: disabled || alreadyEnrolled ? 1 : 3
        }
      }}
      onClick={handleClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Checkbox
            checked={selected}
            disabled={disabled || alreadyEnrolled}
            onChange={() => onToggle(child.id)}
            onClick={(e) => e.stopPropagation()}
            sx={{ mr: 2 }}
            data-testid={`child-checkbox-${child.id}`}
          />
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: 'primary.main',
              mr: 2
            }}
          >
            {child.firstName?.charAt(0)}{child.lastName?.charAt(0)}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {child.firstName} {child.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {child.email}
            </Typography>
          </Box>
        </Box>

        {alreadyEnrolled && (
          <Chip
            label="Already Enrolled"
            size="small"
            color="warning"
            sx={{ mb: 1 }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ChildSelectionCard;
